import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";

export const useToggleNotePin = (): UseMutationResult<
    void,
    Error,
    { noteId: string; caseIdUUID: string }
> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ noteId }) => {
            const response = await fetch(apiUrl("/note/toggle-pin"), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    noteId: noteId,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to toggle pin");
            }
        },
        onSuccess: (_, variables) => {
            // Refetch notes to get updated pin status and proper sorting from server
            // Don't await - fire and forget to avoid race conditions with multiple rapid toggles
            void Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ["case-notes", variables.caseIdUUID],
                }),
                // Also refresh notification history to reflect pin status changes
                queryClient.invalidateQueries({
                    queryKey: ["notification-history"],
                }),
                // Invalidate note details modal cache so it shows updated pin status
                queryClient.invalidateQueries({
                    queryKey: ["note-details", variables.noteId],
                }),
            ]);
        },
    });
};
